import { useCallback } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { sortConversations, type WaConversationLike, type WaMessageLike } from '@agrogruda/shared';
import { waSendMessageResponseSchema, type WaSendMessageResponse } from '@agrogruda/shared/schemas';
import { apiRequest } from '@/lib/api';
import { useSharedChanges } from '@/features/requests/sharedChannel';
import { supabase, type Row } from '@/lib/supabase';

export type WaConversationRow = Row<'wa_conversations'>;
export type WaMessageRow = Row<'wa_messages'>;

/** Conversation with the newest message embedded (list preview: text + time, prototype waLast). */
export interface WaConversationItem extends WaConversationLike {
  status: string;
  last: WaMessageLike | null;
}

export const WA_CONVERSATIONS_KEY = ['wa', 'conversations'] as const;
export const WA_MESSAGES_KEY = ['wa', 'messages'] as const;
export const WA_TOPIC = 'wa-inbox';
const WA_TABLES = ['wa_conversations', 'wa_messages'] as const;
export const waMessagesKey = (conversationId: string) => [...WA_MESSAGES_KEY, conversationId] as const;

/** PostgREST `max_rows` (packages/db/supabase/config.toml). */
const PAGE_SIZE = 1000;

function fail(message: string): never {
  throw new Error(message);
}

export function toMessageLike(
  row: Pick<WaMessageRow, 'id' | 'direction' | 'body' | 'sent_at' | 'status'>,
): WaMessageLike {
  return {
    id: row.id,
    direction: row.direction === 'out' ? 'out' : 'in',
    body: row.body,
    sentAt: row.sent_at,
    status: row.status,
  };
}

type ConversationWithLast = WaConversationRow & {
  wa_messages: Pick<WaMessageRow, 'id' | 'direction' | 'body' | 'sent_at' | 'status'>[];
};

export function toConversationItem(row: ConversationWithLast): WaConversationItem {
  const last = row.wa_messages[0];
  return {
    id: row.id,
    name: row.name,
    phone: row.phone,
    unread: row.unread,
    lastMessageAt: row.last_message_at,
    lastInboundAt: row.last_inbound_at,
    status: row.status,
    last: last ? toMessageLike(last) : null,
  };
}

/** Every conversation, newest first (order last_message_at desc), each with its latest message. */
export async function fetchConversations(): Promise<WaConversationItem[]> {
  const out: WaConversationItem[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await supabase
      .from('wa_conversations')
      .select('*, wa_messages(id, direction, body, sent_at, status)')
      .order('last_message_at', { ascending: false, nullsFirst: false })
      .order('id')
      .order('sent_at', { referencedTable: 'wa_messages', ascending: false })
      .limit(1, { referencedTable: 'wa_messages' })
      .range(from, from + PAGE_SIZE - 1);
    if (error) fail(error.message);
    out.push(...data.map(toConversationItem));
    if (data.length < PAGE_SIZE) break;
  }
  return sortConversations(out);
}

export async function fetchMessages(conversationId: string): Promise<WaMessageLike[]> {
  const out: WaMessageLike[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await supabase
      .from('wa_messages')
      .select('id, direction, body, sent_at, status')
      .eq('conversation_id', conversationId)
      .order('sent_at', { ascending: true })
      .order('created_at', { ascending: true })
      .order('id')
      .range(from, from + PAGE_SIZE - 1);
    if (error) fail(error.message);
    out.push(...data.map(toMessageLike));
    if (data.length < PAGE_SIZE) break;
  }
  return out;
}

export function invalidateWhatsapp(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: WA_CONVERSATIONS_KEY });
  void queryClient.invalidateQueries({ queryKey: WA_MESSAGES_KEY });
}

const EMPTY_CONVERSATIONS: WaConversationItem[] = [];
const EMPTY_MESSAGES: WaMessageLike[] = [];

export interface WaConversationsData {
  conversations: WaConversationItem[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<WaConversationItem[]>;
}

/**
 * Conversations kept live by the `wa_conversations` + `wa_messages` realtime publication (decision D8): an
 * inbound webhook stored by the API shows up without a reload.
 */
export function useWaConversations(enabled = true): WaConversationsData {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: WA_CONVERSATIONS_KEY, queryFn: fetchConversations, enabled });
  useSharedChanges(WA_TOPIC, WA_TABLES, () => invalidateWhatsapp(queryClient), enabled);
  const refresh = useCallback(async () => {
    const rows = await fetchConversations();
    queryClient.setQueryData(WA_CONVERSATIONS_KEY, rows);
    return rows;
  }, [queryClient]);
  return {
    conversations: query.data ?? EMPTY_CONVERSATIONS,
    loading: query.isPending,
    error: query.error ? query.error.message : null,
    refresh,
  };
}

export function useWaMessages(conversationId: string | null): {
  messages: WaMessageLike[];
  loading: boolean;
} {
  const query = useQuery({
    queryKey: waMessagesKey(conversationId ?? ''),
    queryFn: () => fetchMessages(conversationId ?? ''),
    enabled: conversationId !== null,
  });
  return { messages: query.data ?? EMPTY_MESSAGES, loading: conversationId !== null && query.isPending };
}

/** RPC mark_wa_conversation_read (prototype openWaChat: unread = 0). */
export async function markConversationReadRpc(conversationId: string): Promise<void> {
  const { error } = await supabase.rpc('mark_wa_conversation_read', { p_id: conversationId });
  if (error) fail(error.message);
}

/** POST /api/wa/send — the API decides text (24 h window) vs template and stores the outbound row. */
export function sendWaReply(conversationId: string, body: string): Promise<WaSendMessageResponse> {
  return apiRequest('/api/wa/send', waSendMessageResponseSchema, {
    method: 'POST',
    body: { conversationId, body },
  });
}
